import { INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT } from "./constants";

export interface SegmentWaveformLayoutSlot {
	segmentId: string;
	startPercent: number;
	endPercent: number;
	widthPercent: number;
	barCount: number;
}

export interface ResolveSegmentWaveformLayoutInput {
	id: string;
	startPercent: number;
	endPercent: number;
}

export function resolveSegmentWaveformLayout(
	segments: readonly ResolveSegmentWaveformLayoutInput[],
	totalBarBudget = INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT,
): SegmentWaveformLayoutSlot[] {
	if (segments.length === 0) {
		return [];
	}

	const sorted = [...segments].sort(
		(a, b) => a.startPercent - b.startPercent,
	);
	const spans = sorted.map((segment) => {
		const start = Math.max(0, Math.min(100, segment.startPercent));
		const end = Math.max(start, Math.min(100, segment.endPercent));
		return Math.max(0, end - start);
	});
	const totalSpan = spans.reduce((sum, span) => sum + span, 0) || 100;

	const rawBarCounts = spans.map((span) =>
		Math.max(1, Math.round((span / totalSpan) * totalBarBudget)),
	);

	let barSum = rawBarCounts.reduce((sum, count) => sum + count, 0);
	while (barSum > totalBarBudget) {
		const maxIndex = rawBarCounts.indexOf(Math.max(...rawBarCounts));
		rawBarCounts[maxIndex] -= 1;
		barSum -= 1;
	}
	while (barSum < totalBarBudget) {
		const maxSpanIndex = spans.indexOf(Math.max(...spans));
		rawBarCounts[maxSpanIndex] += 1;
		barSum += 1;
	}

	return sorted.map((segment, index) => {
		const start = Math.max(0, Math.min(100, segment.startPercent));
		const end = Math.max(start, Math.min(100, segment.endPercent));
		return {
			segmentId: segment.id,
			startPercent: start,
			endPercent: end,
			widthPercent: end - start,
			barCount: rawBarCounts[index],
		};
	});
}

export function resolveSegmentDividerPercents(
	layout: readonly SegmentWaveformLayoutSlot[],
): number[] {
	return layout
		.map((slot) => slot.startPercent)
		.filter((percent) => percent > 0 && percent < 100);
}
